import { useState, useEffect, useCallback } from "react";
import { clientLogin as apiClientLogin, clientDashboard, clientNotifications, markNotificationRead as apiMarkRead, markAllNotificationsRead as apiMarkAllRead } from "@/lib/api";

interface Client {
  id: number;
  name: string;
  phone: string;
  avatar?: string | null;
  country?: string | null;
  service_plan?: string | null;
  partner_name?: string | null;
  is_single?: boolean | null;
  advisor_name?: string;
  advisor_phone?: string;
}

export interface NotificationItem {
  id: number;
  title: string;
  content: string;
  type: string;
  is_read: boolean;
  created_at: string;
  scheduled_at?: string | null;
}

export function useAuth() {
  const [client, setClient] = useState<Client | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("crm_token");
    const storedClient = localStorage.getItem("crm_client");
    if (storedToken && storedClient) {
      setToken(storedToken);
      try {
        setClient(JSON.parse(storedClient));
      } catch {
        localStorage.removeItem("crm_token");
        localStorage.removeItem("crm_client");
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (phone: string, password: string) => {
    try {
      const data: any = await apiClientLogin(phone, password);

      if (!data || !data.success) {
        return { success: false, error: data?.error || "登录失败" };
      }

      if (data?.success) {
        localStorage.setItem("crm_token", data.token);
        localStorage.setItem("crm_client", JSON.stringify(data.client));
        setToken(data.token);
        setClient(data.client);
        return { success: true };
      }

      return { success: false, error: data?.error || "登录失败" };
    } catch {
      return { success: false, error: "网络错误，请稍后重试" };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_client");
    setToken(null);
    setClient(null);
  }, []);

  const fetchDashboard = useCallback(async () => {
    const storedToken = token || localStorage.getItem("crm_token");
    if (!storedToken) return { success: false, error: "未登录" };

    try {
      const rpcData: any = await clientDashboard(storedToken);

      if (!rpcData || !rpcData.success) {
        return { success: false, error: rpcData?.error || "获取数据失败" };
      }

      const d = rpcData.data || rpcData;
      const progress = (d.progress || []).map((p: any) => ({
        ...p,
        media: Array.isArray(p.media) ? p.media : [],
        documents: Array.isArray(p.documents) ? p.documents : [],
      }));
      const milestones = (d.milestones || []).map((m: any) => ({
        ...m,
        date: m.date,
        media: Array.isArray(m.media) ? m.media : [],
      }));

      return {
        success: true,
        data: {
          client: d.client || client,
          currentStage:
            (d.currentStage as any)?.stage_name ||
            (d.currentStage as any)?.name ||
            (typeof d.currentStage === "string" ? d.currentStage : null) ||
            d.current_stage ||
            null,
          lastUpdated: d.lastUpdated || d.last_updated || null,
          stages: d.stages || [],
          progress,
          milestones,
          geneticMaterials: d.geneticMaterials || d.genetic_materials || [],
        },
      };
    } catch (err: any) {
      return { success: false, error: err?.message || "网络错误，请稍后重试" };
    }
  }, [token, client]);

  const fetchNotifications = useCallback(async (filter?: string) => {
    const storedToken = token || localStorage.getItem("crm_token");
    if (!storedToken) return { success: false, error: "未登录", unreadCount: 0, notifications: [] };

    try {
      const rpcData: any = await clientNotifications(storedToken, filter || "all");

      if (!rpcData || !rpcData.success) {
        return { success: false, error: rpcData?.error || "获取通知失败", unreadCount: 0, notifications: [] };
      }

      return {
        success: true,
        unreadCount: rpcData.unread_count || 0,
        notifications: (rpcData.notifications || []) as NotificationItem[],
      };
    } catch (err: any) {
      return { success: false, error: err?.message || "网络错误", unreadCount: 0, notifications: [] };
    }
  }, [token]);

  const markNotificationRead = useCallback(async (notificationId: number) => {
    const storedToken = token || localStorage.getItem("crm_token");
    if (!storedToken) return { success: false };

    try {
      const rpcData: any = await apiMarkRead(storedToken, notificationId);

      if (!rpcData || !rpcData.success) {
        return { success: false };
      }
      return { success: true };
    } catch {
      return { success: false };
    }
  }, [token]);

  const markAllNotificationsRead = useCallback(async () => {
    const storedToken = token || localStorage.getItem("crm_token");
    if (!storedToken) return { success: false };

    try {
      const rpcData: any = await apiMarkAllRead(storedToken);

      if (!rpcData || !rpcData.success) {
        return { success: false };
      }
      return { success: true, updatedCount: rpcData.updated_count || 0 };
    } catch {
      return { success: false };
    }
  }, [token]);

  return { client, token, loading, login, logout, fetchDashboard, fetchNotifications, markNotificationRead, markAllNotificationsRead };
}